import { useState } from "react";
import { Check, Sparkles, X } from "lucide-react";

import {
  useAcceptSuggestion,
  useAnalyseCompleteness,
  useRejectSuggestion,
  useRequirementSuggestions,
} from "@/api/queries";
import type { RequirementSuggestion } from "@/api/types";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Field, Input } from "@/components/Input";
import { EmptyState, ErrorState, Skeleton } from "@/components/States";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/components/toast-context";
import { ConfidenceIndicator } from "@/features/ingestion/ConfidenceIndicator";
import {
  formatSuggestionBasis,
  formatSuggestionStatus,
  isModelSuggestion,
  pluralize,
} from "@/lib/format";
import { suggestionBasisTone } from "@/lib/stock";

/**
 * Ce qui semble manquer (V1.6-02).
 *
 * Rien ici n'est un besoin. Chaque ligne montre **sur quoi elle repose** et
 * **à quel point c'est établi**, et une proposition du modèle local ne
 * ressemble pas à une relation produit saisie par quelqu'un : c'est le seul
 * indice qu'un lecteur pressé regarde avant de cliquer « Ajouter ».
 *
 * Aucune suggestion n'est ajoutée sans la confirmation explicite d'un humain,
 * qui donne aussi la quantité — le moteur n'en invente pas.
 */
export function SuggestionsPanel({ projectId }: { projectId: string }) {
  const toast = useToast();
  const { data, isLoading, error, refetch } = useRequirementSuggestions(projectId);
  const analyse = useAnalyseCompleteness();
  const [accepting, setAccepting] = useState<RequirementSuggestion | null>(null);

  async function run() {
    try {
      const result = await analyse.mutateAsync({ projectId });
      const pending = result.counts.PENDING ?? 0;
      toast.success(
        "Analyse terminée",
        pending > 0
          ? `${pluralize(pending, "suggestion")} à vérifier.`
          : "Rien d'évident ne manque.",
      );
    } catch (analyseError) {
      toast.error("Analyse impossible", String(analyseError));
    }
  }

  const pending = data?.items.filter((item) => item.status === "PENDING") ?? [];
  const decided = data?.items.filter((item) => item.status !== "PENDING") ?? [];

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[13px] text-ink-muted">
          Des pistes à vérifier. Aucune n'est ajoutée sans vous.
        </p>
        <Button variant="secondary" size="sm" onClick={() => void run()} loading={analyse.isPending}>
          <Sparkles className="size-4" aria-hidden />
          Chercher les oublis
        </Button>
      </div>

      {isLoading && <Skeleton className="h-24" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}

      {data && pending.length === 0 && decided.length === 0 && (
        <EmptyState
          title="Aucune suggestion"
          description="Lancez la recherche : relations produit, chantiers passés et fiches techniques d'abord, le modèle local seulement si rien d'autre ne répond."
        />
      )}

      {pending.length > 0 && (
        <ul className="flex flex-col gap-2">
          {pending.map((suggestion) => (
            <li key={suggestion.id}>
              <SuggestionCard
                projectId={projectId}
                suggestion={suggestion}
                onAccept={() => setAccepting(suggestion)}
              />
            </li>
          ))}
        </ul>
      )}

      {decided.length > 0 && (
        <details className="rounded-lg border border-border bg-surface">
          <summary className="cursor-pointer px-3 py-2 text-[13px] text-ink-muted">
            {pluralize(decided.length, "suggestion")} déjà décidée
            {decided.length > 1 ? "s" : ""}
          </summary>
          <ul className="divide-y divide-border/60">
            {decided.map((suggestion) => (
              <li
                key={suggestion.id}
                className="flex items-center justify-between gap-3 px-3 py-2"
              >
                <span className="min-w-0 truncate text-[13px] text-ink-muted">
                  {suggestion.raw_label}
                </span>
                <span className="shrink-0 text-[12px] text-ink-subtle">
                  {formatSuggestionStatus(suggestion.status)}
                  {suggestion.decided_by ? ` · ${suggestion.decided_by}` : ""}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      <AcceptDialog
        projectId={projectId}
        suggestion={accepting}
        onClose={() => setAccepting(null)}
      />
    </div>
  );
}

function SuggestionCard({
  projectId,
  suggestion,
  onAccept,
}: {
  projectId: string;
  suggestion: RequirementSuggestion;
  onAccept: () => void;
}) {
  const toast = useToast();
  const reject = useRejectSuggestion();
  const fromModel = isModelSuggestion(suggestion.basis);

  return (
    <div
      className={[
        "flex flex-col gap-2 rounded-lg border p-3",
        // Une proposition du modèle est visuellement à part. Elle n'a aucune
        // donnée de HD-Vault derrière elle, et l'écran ne doit pas laisser
        // croire le contraire.
        fromModel
          ? "border-dashed border-warning/50 bg-warning-subtle/30"
          : "border-border bg-surface",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium text-ink">{suggestion.raw_label}</p>
          <p className="text-[12px] text-ink-muted">{suggestion.reason}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <StatusBadge tone={suggestionBasisTone(suggestion.basis)}>
            {formatSuggestionBasis(suggestion.basis)}
          </StatusBadge>
          {/* Le composant canonique du dépôt, pas une seconde échelle de mots :
              « À vérifier » doit vouloir dire la même chose ici et à
              l'ingestion. */}
          <ConfidenceIndicator level={suggestion.confidence} />
        </div>
      </div>

      {suggestion.evidence_quote && (
        <blockquote className="border-l-2 border-border pl-2 text-[12px] italic text-ink-muted">
          « {suggestion.evidence_quote} »
        </blockquote>
      )}

      <p className="text-[11px] text-ink-subtle">Source : {suggestion.source_reference}</p>

      {fromModel && (
        <p className="text-[11px] text-warning">
          Proposition du modèle local. Aucune donnée de HD-Vault ne l'appuie.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" size="sm" onClick={onAccept}>
          <Check className="size-4" aria-hidden />
          Ajouter au besoin
        </Button>
        <Button
          variant="secondary"
          size="sm"
          loading={reject.isPending}
          onClick={() =>
            reject.mutate(
              { projectId, suggestionId: suggestion.id },
              {
                onError: (rejectError) =>
                  toast.error("Suggestion non ignorée", String(rejectError)),
              },
            )
          }
        >
          <X className="size-4" aria-hidden />
          Ignorer
        </Button>
      </div>
    </div>
  );
}

/**
 * Accepter demande une quantité.
 *
 * Le moteur peut en proposer une ; il n'en impose aucune. Un chiffre inventé
 * dans un besoin confirmé est un chiffre faux, et celui-là finit par être
 * commandé.
 */
function AcceptDialog({
  projectId,
  suggestion,
  onClose,
}: {
  projectId: string;
  suggestion: RequirementSuggestion | null;
  onClose: () => void;
}) {
  const toast = useToast();
  const accept = useAcceptSuggestion();
  const [quantity, setQuantity] = useState("1");

  async function submit() {
    if (!suggestion || !quantity) return;
    try {
      await accept.mutateAsync({ projectId, suggestionId: suggestion.id, quantity });
      toast.success("Besoin ajouté", suggestion.raw_label);
      setQuantity("1");
      onClose();
    } catch (error) {
      toast.error("Besoin non ajouté", String(error));
    }
  }

  return (
    <Dialog
      open={suggestion !== null}
      onOpenChange={(next) => {
        if (!next) {
          setQuantity("1");
          onClose();
        }
      }}
      title="Ajouter au besoin"
      description={suggestion?.raw_label ?? ""}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            disabled={!quantity || accept.isPending}
          >
            {accept.isPending ? "Ajout…" : "Ajouter"}
          </Button>
        </>
      }
    >
      <Field label="Quantité" hint="C'est vous qui la décidez.">
        <Input
          type="number"
          min="0"
          step="any"
          inputMode="decimal"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          aria-label="Quantité"
        />
      </Field>
    </Dialog>
  );
}
